/**
 * Visitor-side voice (ARCHITECTURE §H) — browser-to-browser WebRTC with the agent.
 *
 * The visitor's widget keeps a PERSISTENT Twilio Device registered (as soon as voice is configured
 * and the chat session exists), so it can both:
 *  - CALL the handling agent (outbound `start()` → the agent's Client identity), and
 *  - RECEIVE a call the agent places to them (the Device `incoming` event → an accept/decline
 *    prompt). The backend only bridges an agent→visitor call for a visitor in the agent's own org.
 *
 * The token is session-scoped (`/voice-token`, authenticated to this widget session) and refreshed
 * on `tokenWillExpire`. The Device is torn down only on unmount; a finished call leaves it
 * registered so the next call still connects.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { loadTwilioVoice, type TwilioCall, type TwilioDevice } from '@flozic/shared/voice';
import { getWidgetApi } from '../config/loadWidgetConfig';

export type VisitorCallPhase = 'idle' | 'connecting' | 'incoming' | 'active' | 'ended' | 'error';

export interface VisitorCall {
  phase: VisitorCallPhase;
  muted: boolean;
  error: string | null;
  /** True once the softphone Device is registered (voice configured + token obtained). */
  ready: boolean;
  /** Place a call to the handling agent (visitor → agent). */
  start: () => Promise<void>;
  /** Accept an incoming agent → visitor call. */
  accept: () => void;
  hangup: () => void;
  toggleMute: () => void;
  reset: () => void;
}

export function useVisitorCall(cid: string, sessionId: string | null): VisitorCall {
  const [phase, setPhase] = useState<VisitorCallPhase>('idle');
  const [muted, setMuted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  const deviceRef = useRef<TwilioDevice | null>(null);
  const callRef = useRef<TwilioCall | null>(null);
  const agentIdentityRef = useRef<string | null>(null);

  const bindCall = useCallback((call: TwilioCall) => {
    callRef.current = call;
    call.on('accept', () => setPhase('active'));
    const end = () => {
      callRef.current = null;
      setMuted(false);
      setPhase('ended');
    };
    call.on('disconnect', end);
    call.on('cancel', end);
    call.on('reject', end);
    call.on('error', () => {
      callRef.current = null;
      setError('Call failed');
      setPhase('error');
    });
  }, []);

  // Register a persistent Device once we have a session. Best-effort: if voice isn't configured the
  // token endpoint 4xxs and we stay silently unavailable (no call affordances shown).
  useEffect(() => {
    if (!cid || !sessionId) return;
    let disposed = false;
    let device: TwilioDevice | null = null;

    (async () => {
      try {
        const { token, agentIdentity } = await getWidgetApi().widget.voiceToken(cid, sessionId);
        agentIdentityRef.current = agentIdentity ?? null;
        const { Device } = await loadTwilioVoice();
        device = new Device(token, { logLevel: 'error' });
        if (disposed) {
          device.destroy();
          return;
        }
        deviceRef.current = device;
        device.on('registered', () => !disposed && setReady(true));
        device.on('unregistered', () => !disposed && setReady(false));
        device.on('tokenWillExpire', () => {
          getWidgetApi()
            .widget.voiceToken(cid, sessionId)
            .then(({ token: t, agentIdentity: a }) => {
              agentIdentityRef.current = a ?? agentIdentityRef.current;
              device?.updateToken(t);
            })
            .catch(() => {});
        });
        device.on('incoming', (incoming) => {
          const call = incoming as TwilioCall;
          // One call at a time — reject a second while one is live.
          if (callRef.current) {
            call.reject();
            return;
          }
          bindCall(call);
          setError(null);
          setPhase('incoming');
        });
        await device.register();
      } catch {
        if (!disposed) setReady(false); // voice not configured / token unavailable → stay idle
      }
    })();

    return () => {
      disposed = true;
      callRef.current?.disconnect();
      callRef.current = null;
      device?.destroy();
      deviceRef.current = null;
      setReady(false);
    };
  }, [cid, sessionId, bindCall]);

  const start = useCallback(async () => {
    if (!sessionId || phase === 'connecting' || phase === 'active' || phase === 'incoming') return;
    setError(null);
    setPhase('connecting');
    try {
      let device = deviceRef.current;
      let agentIdentity = agentIdentityRef.current;
      // Refresh the token to learn the CURRENT handling agent (claim may have changed since register).
      const res = await getWidgetApi().widget.voiceToken(cid, sessionId);
      agentIdentity = res.agentIdentity ?? agentIdentity;
      agentIdentityRef.current = agentIdentity;
      if (!agentIdentity) throw new Error('No agent is available');
      if (!device) {
        const { Device } = await loadTwilioVoice();
        device = new Device(res.token, { logLevel: 'error' });
        deviceRef.current = device;
        await device.register();
      }
      const call = await device.connect({ params: { To: agentIdentity, callType: 'voip' } });
      bindCall(call);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Calling is not available');
      setPhase('error');
    }
  }, [cid, sessionId, phase, bindCall]);

  const accept = useCallback(() => {
    callRef.current?.accept();
  }, []);

  const hangup = useCallback(() => {
    const call = callRef.current;
    if (call && phase === 'incoming') call.reject();
    else call?.disconnect();
    callRef.current = null;
    setPhase('ended');
  }, [phase]);

  const toggleMute = useCallback(() => {
    const call = callRef.current;
    if (!call) return;
    const next = !muted;
    call.mute(next);
    setMuted(next);
  }, [muted]);

  const reset = useCallback(() => {
    setMuted(false);
    setError(null);
    setPhase('idle');
  }, []);

  return { phase, muted, error, ready, start, accept, hangup, toggleMute, reset };
}
